import fp from 'lodash/fp';
import { toStandardDate, formatDateRange } from '../../helpers/dates';
import apiRequest, {
  prepareRequestOptions,
  composeRequestUrl
} from './api-request';

export const fetchSettings = () => apiRequest('settings');

export const fetchOutreaches = options => {
  const { qs } = options;
  const uri = 'outreaches';
  if (fp.has('outreachOn', qs)) {
    const outreachOn = fp.map(toStandardDate, fp.get('outreachOn', qs));
    return apiRequest(uri, fp.set('qs.outreachOn', outreachOn, options));
  }
  return apiRequest(uri, options);
};

export const fetchOutreachesUpcomimg = options => {
  const { qs } = options;
  const uri = 'outreaches/upcoming';
  if (fp.has('outreachOn', qs)) {
    const outreachOn = fp.map(toStandardDate, fp.get('outreachOn', qs));
    return apiRequest(uri, fp.set('qs.outreachOn', outreachOn, options));
  }
  return apiRequest(uri, options);
};

export const fetchOutreachSchedule = id =>
  apiRequest(`outreach-schedules/${id}`);

export const fetchOutreachSchedules = options => {
  const { qs } = options;
  const uri = 'outreach-schedules';
  if (fp.has('outreachOn', qs)) {
    const outreachOn = fp.map(toStandardDate, fp.get('outreachOn', qs));
    return apiRequest(uri, fp.set('qs.outreachOn', outreachOn, options));
  }
  return apiRequest(uri, options);
};

export const fetchTrackingMethods = () => apiRequest('tracking-methods');

export const fetchUserRoleOptions = () => apiRequest('options/user-roles');

export const fetchUserLocationOptions = () =>
  apiRequest('options/user-locations');

export const fetchAssociatedDomainOptions = () =>
  apiRequest('options/associated-domains');

export const fetchTrackingMethodOptions = () =>
  apiRequest(`options/tracking-methods`);

export const fetchUpcomingOutreachPrograms = guestId =>
  apiRequest(
    `options/upcoming-outreach-programs${guestId ? `?guestId=${guestId}` : ''}`
  );
export const fetchAssociationLinkOptions = () =>
  apiRequest('options/association-links');

export const fetchROItemOptions = () => apiRequest('options/ro-items');

export const fetchClients = options => apiRequest('clients', options);

export const createClient = ({ body }) =>
  apiRequest('clients', { body, method: 'POST' });

export const updateClient = ({ id, body, section }) =>
  apiRequest(`clients/${id}/${section}`, { body, method: 'PUT' });

export const mergeClient = ({ id, sourceId }) =>
  apiRequest(`clients/${id}/merge/${sourceId}`, { method: 'POST' });

export const fetchClient = id => apiRequest(`clients/${id}`);

export const fetchClientByBarcode = barcode =>
  apiRequest(`clients/barcode/${barcode}`);

export const fetchSignature = id => apiRequest(`signatures/${id}`);

export const fetchClientRelatedGuestsForField = ({
  guestId,
  guestRelationField
}) => apiRequest(`clients/${guestId}/related-guests/${guestRelationField}`);

export const fetchCustomList = ({ type }) => apiRequest(`custom-lists/${type}`);

export const fetchClientColumns = () => apiRequest('clients/columns');

export const fetchGuestAuditLogs = ({ guestId, page = 1 }) =>
  apiRequest('audit-logs', { qs: { objType: 'Guest', objId: guestId, page } });

export const fetchGuestNotes = ({ guestId, page = 1 }) =>
  apiRequest('notes', { qs: { objType: 'Guest', objId: guestId, page } });

export const createNote = ({ body }) =>
  apiRequest('notes', { body, method: 'POST' });

export const updateNote = ({ id, body }) =>
  apiRequest(`notes/${id}`, { body, method: 'PUT' });

export const deleteNote = id => apiRequest(`notes/${id}`, { method: 'DELETE' });

export const fetchGuestFiles = ({ guestId, ...query }) =>
  apiRequest('files', { qs: { ...query, objType: 'guest', objId: guestId } });

export const createFile = ({ body }) =>
  apiRequest('files', { body, method: 'POST' });

export const deleteFile = id => apiRequest(`files/${id}`, { method: 'DELETE' });

export const deleteGuest = id =>
  apiRequest(`clients/${id}`, { method: 'DELETE' });

export const fetchGuestVisits = qs => apiRequest('visits', { qs });
export const fetchVisitTrack = ({ id, visitTrackId }) =>
  apiRequest(`visits/${id}/visit-tracks/${visitTrackId}`);

export const fetchGuestReservations = ({ guestId, query: qs }) => {
  const uri = `clients/${guestId}/reservations`;
  if (fp.has('requestTime', qs)) {
    return apiRequest(uri, {
      qs: fp.set('requestTime', formatDateRange(qs.requestTime), qs)
    });
  }
  return apiRequest(uri, { qs });
};

export const fetchVisitColumns = () => apiRequest('visits/columns');

export const fetchVisitOrderByTrackingMethods = ({
  visitId,
  trackingMethodIds
}) =>
  apiRequest(`visits/${visitId}/orders`, {
    qs: { trackingMethodId: trackingMethodIds }
  });

export const fetchVisitConfigs = () => apiRequest('visit-configs');

export const fetchVisitHistory = ({ body }) =>
  apiRequest(`reports/visits/history`, { body, method: 'POST' });

export const createHouseholdMember = ({ guestId, body }) =>
  apiRequest(`clients/${guestId}/household-members`, { body, method: 'POST' });

export const updateHouseholdMember = ({ id, guestId, body }) => {
  const options = { body, method: 'PUT' };
  return apiRequest(`clients/${guestId}/household-members/${id}`, options);
};

export const deleteHouseholdMember = ({ id, guestId }) =>
  apiRequest(`clients/${guestId}/household-members/${id}`, {
    method: 'DELETE'
  });

export const fetchHouseholdMemberColumns = () =>
  apiRequest('household-members/columns');

export const fetchReportForOutreachAggregate = ({ body }) =>
  apiRequest('reports/outreach-aggregate', { body, method: 'POST' });

export const fetchOutreachAggregateOptions = () =>
  apiRequest('reports/outreach-aggregate/options');

// Report Templates
export const fetchReportTemplates = options =>
  apiRequest('report-templates', options);

export const createReportTemplate = ({ body }) =>
  apiRequest('report-templates', { body, method: 'POST' });

export const updateReportTemplate = ({ id, body }) =>
  apiRequest(`report-templates/${id}`, { body, method: 'PUT' });

export const deleteReportTemplate = id =>
  apiRequest(`report-templates/${id}`, { method: 'DELETE' });
// -----

// Download Requests
export const fetchDownloadRequests = options =>
  apiRequest('download-requests', options);

export const fetchDownloadRequest = id => apiRequest(`download-requests/${id}`);

export const createDownloadRequest = ({ body }) =>
  apiRequest('download-requests', { body, method: 'POST' });

export const fetchDownloadRequestResult = id =>
  apiRequest(`download-requests/${id}/content`);

export const fetchDownloadRequestsSearchOptions = options =>
  apiRequest('download-requests/search-options', options);

export const deleteDownloadRequest = ({ id, context }) =>
  apiRequest(`download-requests/${id}/${context}`, { method: 'DELETE' });
// -----

// Inventory Items
export const fetchInventoryItem = id => apiRequest(`inventory/items/${id}`);

export const fetchInventoryItems = options =>
  apiRequest('inventory/items', options);

export const createInventoryItem = ({ body }) =>
  apiRequest('inventory/items', { body, method: 'POST' });

export const updateInventoryItem = ({ id, body }) =>
  apiRequest(`inventory/items/${id}`, { body, method: 'PUT' });

export const deleteInventoryItem = id =>
  apiRequest(`inventory/items/${id}`, { method: 'DELETE' });

export const adjustInventoryItem = ({ id, body }) =>
  apiRequest(`inventory/items/${id}/adjust`, { body, method: 'POST' });

export const fetchInventoryItemAvailability = id =>
  apiRequest(`inventory/items/${id}/availabilities`);

export const deleteInventoryItemAvailability = ({ id, locationId, zoneId }) =>
  apiRequest(`inventory/items/${id}/availabilities/${locationId}/${zoneId}`, {
    method: 'DELETE'
  });

export const fetchInventoryItemAvailabilityByLocation = ({
  id,
  locationId,
  zoneId
}) =>
  apiRequest(`inventory/items/${id}/availabilities/${locationId}/${zoneId}`, {
    method: 'GET'
  });

export const fetchInventoryItemsAvailability = options =>
  apiRequest('inventory/items/availabilities', options);

export const fetchInventoryItemSkus = id =>
  apiRequest(`inventory/items/${id}/skus`);

export const addInventoryItemSku = ({ id, body }) =>
  apiRequest(`inventory/items/${id}/skus`, { body, method: 'POST' });

export const deleteInventoryItemSku = ({ id, skuId }) =>
  apiRequest(`inventory/items/${id}/skus/${skuId}`, { method: 'DELETE' });
// -----

// Inventory Packages
export const fetchInventoryPackage = id =>
  apiRequest(`inventory/packages/${id}`);

export const fetchInventoryPackages = options =>
  apiRequest('inventory/packages', options);

export const createInventoryPackage = ({ body }) =>
  apiRequest('inventory/packages', { body, method: 'POST' });

export const updateInventoryPackage = ({ id, body }) =>
  apiRequest(`inventory/packages/${id}`, { body, method: 'PUT' });

export const deleteInventoryPackage = id =>
  apiRequest(`inventory/packages/${id}`, { method: 'DELETE' });

export const fetchInventoryPackageItems = id =>
  apiRequest(`inventory/packages/${id}/items`);

export const addInventoryPackageItem = ({ id, ...body }) =>
  apiRequest(`inventory/packages/${id}/items`, { method: 'POST', body });

export const updateInventoryPackageItem = ({ id, packageItemId, quantity }) =>
  apiRequest(`inventory/packages/${id}/items/${packageItemId}`, {
    method: 'PUT',
    body: { quantity }
  });

export const deleteInventoryPackageItem = ({ id, packageItemId }) =>
  apiRequest(`inventory/packages/${id}/items/${packageItemId}`, {
    method: 'DELETE'
  });

export const adjustInventoryPackage = ({ id, body }) =>
  apiRequest(`inventory/packages/${id}/adjust`, { body, method: 'POST' });

export const fetchInventoryPackagesAvailability = options =>
  apiRequest('inventory/packages/availabilities', options);

export const fetchInventoryPackageAvailability = id =>
  apiRequest(`inventory/packages/${id}/availabilities`);

export const deleteInventoryPackageAvailability = ({ id, locationId }) =>
  apiRequest(`inventory/packages/${id}/availabilities/${locationId}`, {
    method: 'DELETE'
  });

export const fetchInventoryPackageAvailabilityByLocation = ({
  id,
  locationId
}) =>
  apiRequest(`inventory/packages/${id}/availabilities/${locationId}`, {
    method: 'GET'
  });

export const fetchInventoryPackageActiveReservationOrderItemsCount = id =>
  apiRequest(`inventory/packages/${id}/active-reservation-order-items/count`);

export const fetchInventoryPackageSkus = id =>
  apiRequest(`inventory/packages/${id}/skus`);

export const addInventoryPackageSku = ({ id, body }) =>
  apiRequest(`inventory/packages/${id}/skus`, { body, method: 'POST' });

export const deleteInventoryPackageSku = ({ id, skuId }) =>
  apiRequest(`inventory/packages/${id}/skus/${skuId}`, { method: 'DELETE' });
// -----

// Inventory SKUs
export const fetchInventorySkuBySku = sku =>
  apiRequest(`inventory/skus/sku/${sku}`);
// -----

// Inventory Tags
export const fetchInventoryTags = options =>
  apiRequest('inventory/tags', options);

export const createInventoryTag = ({ body }) =>
  apiRequest('inventory/tags', { body, method: 'POST' });

export const updateInventoryTag = ({ id, body }) =>
  apiRequest(`inventory/tags/${id}`, { body, method: 'PUT' });

export const deleteInventoryTag = id =>
  apiRequest(`inventory/tags/${id}`, { method: 'DELETE' });

export const fetchInventoryTagItemCount = id =>
  apiRequest(`inventory/tags/${id}/items/count`, { method: 'GET' });
// -----

// Inventory Locations
export const fetchInventoryLocation = id =>
  apiRequest(`inventory/locations/${id}`);

export const fetchInventoryLocations = options =>
  apiRequest('inventory/locations', options);

export const createInventoryLocation = ({ body }) =>
  apiRequest('inventory/locations', { body, method: 'POST' });

export const updateInventoryLocation = ({ id, body }) =>
  apiRequest(`inventory/locations/${id}`, { body, method: 'PUT' });

export const deleteInventoryLocation = id =>
  apiRequest(`inventory/locations/${id}`, { method: 'DELETE' });
// -----

// Inventory LocationZones
export const fetchInventoryLocationZones = options =>
  apiRequest('inventory/location-zones', options);

export const createInventoryLocationZone = ({ body }) =>
  apiRequest('inventory/location-zones', { body, method: 'POST' });

export const updateInventoryLocationZone = ({ id, body }) =>
  apiRequest(`inventory/location-zones/${id}`, { body, method: 'PUT' });

export const deleteInventoryLocationZone = id =>
  apiRequest(`inventory/location-zones/${id}`, { method: 'DELETE' });
// -----

// Inventory Options
export const fetchInventoryTagOptions = () =>
  apiRequest('inventory/options/tags');

export const fetchInventoryItemOptions = () =>
  apiRequest('inventory/options/items');

export const fetchInventoryPackageOptions = () =>
  apiRequest('inventory/options/packages');

export const fetchInventoryActionOptions = () =>
  apiRequest('inventory/options/actions');

export const fetchInventoryLocationOptions = () =>
  apiRequest('inventory/options/locations');

export const fetchInventoryLocationZoneOptions = () =>
  apiRequest('inventory/options/location-zones');

export const fetchInventoryItemTagsOptions = () =>
  apiRequest('inventory/options/item-tags');

export const fetchInventoryPackageTagsOptions = () =>
  apiRequest('inventory/options/package-tags');

export const fetchInventoryItemLocationZoneOptions = () =>
  apiRequest('inventory/options/item-location-zones');

export const fetchInventoryItemAvailabilityLocationZoneOptions = () =>
  apiRequest('inventory/options/item-availability-location-zones');

export const fetchInventoryPackageAvailabilityLocationOptions = () =>
  apiRequest('inventory/options/package-availability-locations');

export const fetchInventoryAvailabilityOptions = () =>
  apiRequest('inventory/options/availabilities');

export const fetchInventoryDonorOptions = () =>
  apiRequest('inventory/options/donors');
// -----

// Inventory AuditLogs
export const fetchInventoryAuditLogs = options => {
  const uri = 'inventory/audit-logs';
  const { qs } = options;
  if (fp.has('createdAt', qs)) {
    const createdAt = fp.map(toStandardDate, fp.get('createdAt', qs));
    return apiRequest(uri, fp.set('qs.createdAt', createdAt, options));
  }

  return apiRequest(uri, options);
};

export const fetchInventoryAuditLogsForItem = options => {
  const { qs } = options;
  const uri = `inventory/items/${qs.itemId}/audit-logs`;
  if (fp.has('createdAt', qs)) {
    const createdAt = fp.map(toStandardDate, fp.get('createdAt', qs));
    return apiRequest(uri, fp.set('qs.createdAt', createdAt, options));
  }
  return apiRequest(uri, options);
};

export const fetchInventoryAuditLogsSearchOptions = () =>
  apiRequest('inventory/audit-logs/search-options');

// Inventory Reports
export const fetchGuestSummaryReport = body =>
  apiRequest('inventory/reports/guest-summary', { method: 'POST', body });

export const fetchDonorSummaryReport = body =>
  apiRequest('inventory/reports/donor-summary', { method: 'POST', body });

export const fetchLowStockItems = body =>
  apiRequest('inventory/reports/low-stock-items', { method: 'POST', body });

export const fetchItemsAndPackagesSkus = body =>
  apiRequest('inventory/reports/items-and-packages-skus', {
    method: 'POST',
    body
  });

// POS
export const fetchPosOrderByVisitTrackId = visitTrackId =>
  apiRequest('pos/orders', { qs: { visitTrackId } });

export const createPosOrder = ({ body }) =>
  apiRequest('pos/orders', { method: 'POST', body });

export const completePosOrder = id =>
  apiRequest(`pos/orders/${id}/complete`, { method: 'POST' });

export const cancelPosOrder = ({ id, body }) =>
  apiRequest(`pos/orders/${id}/cancel`, { method: 'POST', body });

export const adjustPosOrder = ({ id, body }) =>
  apiRequest(`pos/orders/${id}/adjust`, { method: 'POST', body });

// body: { quantity, itemId, locationZoneId }
export const addPosOrderItem = ({ orderId, body }) =>
  apiRequest(`pos/orders/${orderId}/items`, { method: 'POST', body });

export const updatePosOrderItem = ({ orderId, itemId, body }) =>
  apiRequest(`pos/orders/${orderId}/items/${itemId}`, { method: 'PUT', body });

export const deletePosOrderItem = ({ orderId, itemId }) =>
  apiRequest(`pos/orders/${orderId}/items/${itemId}`, { method: 'DELETE' });

export const deletePosOrderItems = orderId =>
  apiRequest(`pos/orders/${orderId}/items`, { method: 'DELETE' });

export const searchPosItems = options =>
  apiRequest('pos/search-items', options);

// Permissions
export const fetchUserPermissions = () => apiRequest('permissions/user-access');

// Export
export const fetchInventoryExportUrl = ({ type, options }) => {
  const qs = fp.omitBy(each => !each, options);
  const createdAt = fp.get('createdAt', qs);
  const requestOptions = createdAt
    ? { qs: fp.set('createdAt', fp.map(toStandardDate, createdAt), qs) }
    : { qs };
  return apiRequest(`inventory/exports/${type}`, requestOptions);
};

export const fetchInventoryItemsTemplateDownloadUrl = () =>
  apiRequest('inventory/import/items-template');

// AWS S3
export const awsS3presignedRequest = async ({
  method = 'POST',
  ...restOptions
}) => {
  const url = composeRequestUrl('aws-presigned');
  const options = prepareRequestOptions({ method, ...restOptions });
  const response = await fetch(url, options);
  return response.json();
};

// Reservation
export const createReservation = ({ body }) =>
  apiRequest('reservations', { method: 'POST', body });

export const updateReservation = ({ id, body }) =>
  apiRequest(`reservations/${id}`, { method: 'PUT', body });

export const fetchReservation = id => apiRequest(`reservations/${id}`);

export const fetchReservationsWithOnholdItems = options =>
  apiRequest('reservations/with-onhold-items', options);

export const createReservationOrders = ({ id, body }) =>
  apiRequest(`reservations/${id}/orders`, { method: 'POST', body });

export const updateReservationOrders = ({ id, body }) =>
  apiRequest(`reservations/${id}/orders`, { method: 'PUT', body });

export const fetchReservationPicklists = id =>
  apiRequest(`reservations/${id}/picklists`);

export const fetchReservationToday = options =>
  apiRequest('reservations/today', options);

export const fetchReservationUpcoming = options => {
  const uri = 'reservations/upcoming';
  const { qs } = options;
  if (fp.has('requestTime', qs)) {
    const requestTime = formatDateRange(qs.requestTime);
    return apiRequest(uri, fp.set('qs.requestTime', requestTime, options));
  }

  return apiRequest(uri, options);
};

export const fetchReservationPending = options => {
  const uri = 'reservations/upcoming-pending';
  const { qs } = options;
  if (fp.has('requestTime', qs)) {
    const requestTime = formatDateRange(qs.requestTime);
    return apiRequest(uri, fp.set('qs.requestTime', requestTime, options));
  }

  return apiRequest(uri, options);
};

export const fetchReservationArchived = options => {
  const uri = 'reservations/archived';
  const { qs } = options;
  if (fp.has('requestTime', qs)) {
    const requestTime = formatDateRange(qs.requestTime);
    return apiRequest(uri, fp.set('qs.requestTime', requestTime, options));
  }

  return apiRequest(uri, options);
};

export const updateReservationStatus = ({ id, statusAction, body }) =>
  apiRequest(`reservations/${id}/status/${statusAction}`, {
    method: 'PUT',
    body: body || {}
  });

export const fetchReservationExportUrl = ({ type, options }) => {
  const qs = fp.omitBy(each => !each, options);
  const requestTime = fp.get('requestTime', qs);
  const requestOptions = requestTime
    ? { qs: fp.set('requestTime', fp.map(toStandardDate, requestTime), qs) }
    : { qs };
  return apiRequest(`reservations/exports/${type}`, requestOptions);
};

export const fetchReservationOrderedItemsReport = body =>
  apiRequest(`reservations/reports/ordered-items`, { method: 'POST', body });

export const fetchReservationsOrderItems = options =>
  apiRequest(`reservation-order-items`, options);

export const fetchROItems = options =>
  apiRequest('reservation-ordering/items', options);

export const fetchROItem = id => apiRequest(`reservation-ordering/items/${id}`);

export const createROItem = ({ body }) =>
  apiRequest('reservation-ordering/items', { method: 'POST', body });

export const updateROItem = ({ id, body }) =>
  apiRequest(`reservation-ordering/items/${id}`, { method: 'PUT', body });

export const deleteROItem = id =>
  apiRequest(`reservation-ordering/items/${id}`, { method: 'DELETE' });

export const fetchROItemAssocPicklistCount = id =>
  apiRequest(`reservation-ordering/items/${id}/assoc-picklists/count`, {
    method: 'GET'
  });

export const fetchROPicklists = options =>
  apiRequest('reservation-ordering/picklists', options);

export const fetchROPicklist = id =>
  apiRequest(`reservation-ordering/picklists/${id}`);

export const createROPicklist = ({ body }) =>
  apiRequest('reservation-ordering/picklists', { method: 'POST', body });

export const updateROPicklist = ({ id, body }) =>
  apiRequest(`reservation-ordering/picklists/${id}`, { method: 'PUT', body });

export const deleteROPicklist = id =>
  apiRequest(`reservation-ordering/picklists/${id}`, { method: 'DELETE' });

// Settings -> Portal
export const fetchCurrentPantry = () => apiRequest(`pantries/current`);

export const createPantry = ({ body }) =>
  apiRequest(`pantries`, { method: 'POST', body });

export const updatePantry = ({ id, body }) =>
  apiRequest(`pantries/${id}`, { method: 'PUT', body });

export const fetchSmtpAccounts = ({ type }) =>
  apiRequest(`smtp-accounts?type=${type}`);

export const createSmtpAccount = ({ body }) =>
  apiRequest(`smtp-accounts`, { method: 'POST', body });

export const updateSmtpAccount = ({ id, body }) =>
  apiRequest(`smtp-accounts/${id}`, { method: 'PUT', body });

export const deleteSmtpAccount = id =>
  apiRequest(`smtp-accounts/${id}`, { method: 'DELETE' });

export const fetchPortalUsers = options => apiRequest('portal-users', options);

export const syncPortalUserGuestAddress = id =>
  apiRequest(`portal-users/${id}/sync-guest-address`, { method: 'POST' });

export const syncPortalUserGuestPhone = id =>
  apiRequest(`portal-users/${id}/sync-guest-phone`, { method: 'POST' });

// Settings -> POS
export const fetchPosTrackingMethodLocations = options =>
  apiRequest('pos/tracking-method-locations', options);

export const fetchPosLocationsForTrackingMethod = trackingMethodId =>
  apiRequest(`pos/tracking-method-locations/${trackingMethodId}`);

export const createPosLocationsForTrackingMethod = ({ id, body }) =>
  apiRequest(`pos/tracking-method-locations/${id}`, {
    method: 'POST',
    body
  });

export const deletePosLocationsForTrackingMethod = ({ id, body }) =>
  apiRequest(`pos/tracking-method-locations/${id}`, {
    method: 'DELETE',
    body
  });

// MFA / Login (unauthenticated)
export const requestOtp = ({ username, password, location }) =>
  apiRequest('members/request-mfa', {
    method: 'POST',
    body: { username, password, location },
    skipAuth: true
  });

export const verifyOtp = ({ username, location, code }) =>
  apiRequest('members/verify-mfa', {
    method: 'POST',
    body: { username, location, code },
    skipAuth: true
  });

export const forgotPassword = ({ username }) =>
  apiRequest('members/forgot-password', {
    method: 'POST',
    body: { username },
    skipAuth: true
  });
